import { ColDef } from "ag-grid-community";
import { UIViewEnum } from "../../../common/enums/UIViewEnum";
import { IBaseGridColumnDefinition } from "../../../common/models/BaseGridColumnDefinition";
import { RowDef } from "../../../components/ag-grid-extensions/models/RowDef";
import { SideSheetGroupedSelectOption } from "../../../components/side-sheet/side-sheet-grouped-select/SideSheetGroupedSelect";
import { createNumericColumn, createRecentMemoColumn, createShortcutsColumn, createTextColumn, createTimestampColumn } from "../../../components/summary-listing-grid/ColumnCreators";
import { IRoute, routes } from "../../../routes/routes";
import { PageConfigurationAccessEnum, PageConfigurationDataModel3, PageConfigurationModel3 } from "../../../services/page-configuration-service/Models";
import { IWellSummaryGridCell, IWellSummaryGridRow, IWellSummaryModel } from "../../../services/summary-listing-data-service/WellSummaryModels";
import { dataValueCellStyler, getDisplayText, getRoute, hasHRef, nameCellStyler } from "../common/Common";
import { SummaryListingSortTypeEnum } from "../../../common/enums/SummaryListingSortTypeEnum";

export type WellsViewData = {
    wellSummary: IWellSummaryModel;
    pageConfigData?: PageConfigurationDataModel3;
};

export type WellRow = RowDef & {
    wellID: number;
    wellName: string;
    hasCommError: boolean;
    hasRecentMemo: boolean;
    memoText: string;
    memoTimestamp: string;
    alarmStatus: number;
    functionIcons: IRoute[];
    // cell0...celln: StandardCellData
};

export function createViewOptions(configurations: PageConfigurationModel3[] | undefined, access: PageConfigurationAccessEnum, defaultID: number) {
    return configurations?.filter(config => config.access === access)
        .map(configuration => {
            return {
                label: configuration.name as string,
                value: configuration.id as number,
                isDefault: configuration.id === defaultID
            };
        })
        ?? new Array<SideSheetGroupedSelectOption>();
}

export function createGridData(summaryData: IWellSummaryModel | undefined, 
    isPhone: boolean, 
    groupID: number,
    showShortcuts: boolean,
    sortType: SummaryListingSortTypeEnum) {

    const pinnedColumnCount: number = (summaryData?.gridData?.lockedColumnCount ?? 0) + (showShortcuts ? 1 : 0) + (!isPhone ? 1 : 0);
    const cols: Array<ColDef> = createGridColumns(summaryData, pinnedColumnCount, !isPhone, showShortcuts, sortType);
    const rows: Array<RowDef> = createGridRows(summaryData, groupID);
    const data = cols && rows
        ? {
            rows: rows,
            cols: cols
        } : {
            rows: new Array<RowDef>(),
            cols: new Array<ColDef>()
        };
    return data;
}

export function createGridRows(dataResponse: IWellSummaryModel | undefined, groupID: number): Array<WellRow> {
    return dataResponse?.gridData?.rows.map((row: IWellSummaryGridRow) => {
        let newRow: WellRow = {
            wellID: row.wellID,
            wellName: row.wellName,
            hasCommError: row.hasCommError,
            hasRecentMemo: row.hasRecentMemo,
            memoText: row.memoText,
            memoTimestamp: row.memoTimestamp,
            memoID: row.memoID,
            alarmStatus: row.alarmStatus,
            functionIcons: getShortcutData(row),
            order: row.order,
            active: row.active,
        };

        row.cells.forEach((cell: IWellSummaryGridCell, index: number) => {
            newRow[`cell${index}`] = cell;
            newRow[`cell${index}`].displayText = getDisplayText(cell);

            // convert the timestamp property from string to Date
            if(typeof (newRow[`cell${index}`].timestamp) === "string") {
                newRow[`cell${index}`].timestamp = new Date(newRow[`cell${index}`].timestamp);
            }

            if (hasHRef(cell)) {
                newRow[`cell${index}`].href = getRoute(row, cell, groupID);
            }
        });

        return newRow;
    }) ?? new Array<WellRow>();
}

export function createGridColumns(dataResponse: IWellSummaryModel | undefined, pinnedColumnCount: number, isDesktop: boolean, showShortcuts: boolean, sortType: SummaryListingSortTypeEnum): Array<ColDef> {
    const hasShortcuts: boolean = isDesktop;
    const hasMemos: boolean = isDesktop;
    let cellIndex = 0;

    return dataResponse?.gridData?.columns
        .filter(
            col => ((hasShortcuts && showShortcuts) || col.field !== "functionIcons") &&
                (hasMemos || (col.field !== "hasRecentMemo"))
        )
        .map((col: IBaseGridColumnDefinition) => {
            if (col.field === "hasRecentMemo") {

                return createRecentMemoColumn({
                    field: col.field,
                    headerName: "Memo",
                    initialPinned: (pinnedColumnCount > 0) ? "left" : false
                });
            } else if (col.field === "functionIcons") {

                return createShortcutsColumn({
                    field: col.field,
                    headerName: col.headerText,
                    initialPinned: (pinnedColumnCount > 0) ? "left" : false
                });
            } else if (col.field.indexOf("dataValue") !== -1) {

                const index = cellIndex++;

                return createNumericColumn({
                    field: `cell${index}`,
                    headerName: col.headerText,
                    styler: dataValueCellStyler,
                    initialPinned: isDesktop && (index < pinnedColumnCount) ? "left" : false,
                    footerAggregate: col.aggregate
                });
            } else if (col.field.indexOf("timestamp") !== -1) {

                const index = cellIndex++;

                return createTimestampColumn({
                    field: `cell${index}`,
                    headerName: col.headerText,
                    initialPinned: isDesktop && (index < pinnedColumnCount) ? "left" : false,
                });
            } else {
                const index = cellIndex++;
                const isNameColumn: boolean = (
                    // DeviceName (for backward compatability)
                    (col.equatableKey?.startsWith("0:2:") ||
                    // DeviceDisplayName
                    col.equatableKey?.startsWith("0:3:") || // WellName
                    col.equatableKey?.startsWith("0:64:"))) ?? false;

                return createTextColumn({
                    field: `cell${index}`,
                    headerName: col.headerText,
                    styler: isNameColumn ? nameCellStyler : undefined,
                    initialSort: sortType === SummaryListingSortTypeEnum.Alphanumeric && isNameColumn ? "asc" : undefined,
                    initialPinned: ((!isDesktop && isNameColumn) || (isDesktop && index < pinnedColumnCount)) ? "left" : false,
                    lockPinned: !isDesktop,
                    lockPosition: !isDesktop && isNameColumn,
                    lockVisible: !isDesktop && isNameColumn,
                    footerAggregate: col.aggregate
                });
            }
        }) ?? new Array<ColDef>();
}

const addRoute = (viewID: UIViewEnum, shortcuts: IRoute[]) => {
    const route = routes.find(o => o.viewID === viewID);
    if (route) {
        shortcuts.push(route);
    }
};
export function getShortcutData(row: IWellSummaryGridRow): IRoute[] {

    const shortcuts: IRoute[] = [];

    if (row.hasWellTrending) {
        addRoute(UIViewEnum.Well_Graph, shortcuts);
    }

    return shortcuts;
}

